import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, Checkbox, ModalLayout, Textfield } from '@aimsos/ui';

import { useUpdateWorkspaceOwner } from '../hooks';

const simpleEmailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());

/**
 * Change workspace owner — POST /v1/workspace/update with `workspace_uuid` + `owner_email`.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.workspaceId
 * @param {() => void} [props.onUpdated] — after successful save (e.g. refetch list + detail)
 */
const ChangeWorkspaceOwnerModal = ({ isOpen, onClose, workspaceId, onUpdated }) => {
  const { t } = useTranslation('workspaces');
  const updateWorkspaceOwner = useUpdateWorkspaceOwner();
  const titleId = useId();
  const confirmId = useId();

  const [ownerEmail, setOwnerEmail] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const reset = useCallback(() => {
    setOwnerEmail('');
    setConfirmed(false);
    setSubmitted(false);
    setSubmitting(false);
    setSubmitError(null);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    reset();
  }, [isOpen, reset]);

  const emailError =
    submitted && !ownerEmail.trim()
      ? t('detail.changeOwnerModal.emailRequired')
      : submitted && !simpleEmailValid(ownerEmail)
        ? t('detail.changeOwnerModal.emailInvalid')
        : '';

  const canSave =
    confirmed && ownerEmail.trim() && simpleEmailValid(ownerEmail) && !submitting;

  const handleDismiss = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleDismiss]);

  const handleSubmit = useCallback(async () => {
    setSubmitted(true);
    setSubmitError(null);
    const emailOk = ownerEmail.trim() && simpleEmailValid(ownerEmail);
    if (!confirmed || !emailOk || !workspaceId) return;

    setSubmitting(true);
    try {
      await updateWorkspaceOwner(workspaceId, ownerEmail);
      onUpdated?.();
      reset();
      onClose?.();
    } catch (err) {
      setSubmitError(err?.message ?? t('detail.changeOwnerModal.submitError'));
    } finally {
      setSubmitting(false);
    }
  }, [confirmed, ownerEmail, workspaceId, onClose, onUpdated, reset, t, updateWorkspaceOwner]);

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleDismiss}
      title={t('detail.changeOwnerModal.title')}
      subtitle={t('detail.changeOwnerModal.subtitle')}
      titleId={titleId}
      closeAriaLabel={t('detail.changeOwnerModal.closeAria')}
      maxWidthClassName="max-w-lg"
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="mx-auto flex w-full max-w-md flex-col items-center gap-5 text-center">
        <div className="flex w-full items-start gap-3 text-left">
          <Checkbox
            id={confirmId}
            checked={confirmed}
            onChange={() => setConfirmed((c) => !c)}
            disabled={submitting}
            className="mt-0.5 shrink-0"
            aria-label={t('detail.changeOwnerModal.confirmAria')}
          />
          <label htmlFor={confirmId} className="cursor-pointer text-sm leading-snug text-content-secondary">
            {t('detail.changeOwnerModal.confirmLabel')}
          </label>
        </div>

        <div className="w-full">
          <Textfield
            label={t('detail.changeOwnerModal.emailLabel')}
            placeholder={t('detail.changeOwnerModal.emailPlaceholder')}
            value={ownerEmail}
            onChange={setOwnerEmail}
            state={emailError ? 'error' : 'default'}
            supportingText={emailError || undefined}
            disabled={submitting}
          />
        </div>

        {submitError ? (
          <p className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {submitError}
          </p>
        ) : null}
        </div>
      </div>

      <div className="shrink-0 bg-surface-muted/20 px-5 py-4">
        <div className="flex justify-center gap-4">
          <Button variant="secondary" type="button" size="md" onClick={handleDismiss} disabled={submitting}>
            {t('detail.changeOwnerModal.cancel')}
          </Button>
          <Button
            variant="danger"
            type="button"
            size="md"
            loading={submitting}
            disabled={!canSave}
            onClick={handleSubmit}
          >
            {submitting ? t('detail.changeOwnerModal.saving') : t('detail.changeOwnerModal.save')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
};

export default ChangeWorkspaceOwnerModal;
